import { Box, TextField, Typography } from '@mui/material';
import { ApolloQueryResult, OperationVariables, useMutation } from '@parkchirp/api-client';
import React, { useCallback } from 'react';

import { ADD_TENANT_TO_SUBSCRIPTION } from '../../mutations';
import { SetShowSnackbar } from '../../types';
import { isValidEmail } from '../../utils';
import { EditorModal } from '../modals';

interface AddTenantPayorModalProps {
	subscriptionId: string;
	handleClose: () => void;
	refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	payorEmail: string;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	theme?: any;
}

export const AddTenantPayorModal = ({
	subscriptionId,
	handleClose,
	refetch,
	payorEmail,
	setShowSnackbar,
	tenantSubscriptionId,
	theme,
}: AddTenantPayorModalProps) => {
	const [addTenant, { loading }] = useMutation(ADD_TENANT_TO_SUBSCRIPTION);

	const handleSubmit = useCallback(async () => {
		try {
			if (!isValidEmail(payorEmail.trim())) {
				setShowSnackbar({
					message: `Payor email is invalid.`,
					severity: 'error',
					autoHideDuration: 15000,
					onClose: () => setShowSnackbar(null),
				});
				return;
			}
			const response = await addTenant({
				variables: {
					email: payorEmail.trim().toLocaleLowerCase(),
					subscriptionId,
					tenantSubscriptionId,
				},
				context: { apiVersion: 'v2' },
			});
			if (!response?.data?.addTenantToSubscription?.id) {
				throw new Error('Failed to add tenant user');
			}
			setShowSnackbar({
				message: 'Successfully added payor to tenant users',
				severity: 'success',
				autoHideDuration: 5000,
				onClose: () => setShowSnackbar(null),
			});
			refetch();
			handleClose();
		} catch {
			setShowSnackbar({
				message: 'Failed to add payor to tenant users',
				severity: 'error',
				autoHideDuration: 15000,
				onClose: () => setShowSnackbar(null),
			});
		}
	}, [setShowSnackbar, refetch, handleClose, payorEmail, addTenant, subscriptionId, tenantSubscriptionId]);

	return (
		<EditorModal
			handleClose={() => handleClose()}
			handleSave={handleSubmit}
			hasUnsavedChanges={false}
			headerText='Add Payor'
			containerClassName='addTenantModal'
			loading={loading}
			headerBorder
			theme={theme}
		>
			<Box style={{ margin: '10px 0' }}>
				<Typography style={{ margin: '20px 0' }}>
					{`Enable tenant portal for the subscription's payor?`}
				</Typography>
				<TextField
					id='tenant-payor email'
					label='Payor Email'
					value={payorEmail}
					style={{ width: '100%' }}
					variant='outlined'
					autoComplete='off'
					inputProps={{ 'data-cy': 'payor-tenant-email-input' }}
				/>
			</Box>
		</EditorModal>
	);
};
